import {
    GetPostcodeLookupResponse,
    GetStateByStateKeyResponse
} from '@iwoca/lapi-client/edge';
import { RootState } from '@iwProvider/configureStore';
import _ from 'lodash';
import { v4 as uuidv4 } from 'uuid';

import { companyTypesDE, companyTypesUK } from '../../helpers/companyTypes';
import { getCountry } from '../../iwIntlProvider/CountryProvider';

type LAPIPeople = NonNullable<
    NonNullable<GetStateByStateKeyResponse['data']['application']>['people']
>;

type LAPIPerson = LAPIPeople[number];

type Company = NonNullable<
    GetStateByStateKeyResponse['data']['application']
>['company'];

export type Person = {
    uid: string;
    title?: string;
    firstName?: string;
    lastName?: string;
    email: string;
    emailUID: string;
    dateOfBirth?: {
        day?: string | null;
        month?: string | null;
        year?: string | null;
    };
    roles: LAPIPerson['roles'];
    isApplicant: boolean;
    isGuarantor: boolean;
    isDirector: boolean;
    isShareholder: boolean;
    phones?: { uid?: string; type?: string; number?: string }[];
    marketing: boolean;
    addresses?: {
        uid: string;
        town?: string;
        postcode?: string;
        house_number?: string;
        house_name?: string;
        street_line_1?: string;
        street_line_2?: string;
        residential_status?:
            | 'tenant'
            | 'owner_with_mortgage'
            | 'owner_no_mortgage'
            | 'rent_free';
        equifax_token?: string;
        country?: 'GB' | 'DE' | 'PL' | 'OTHER';
        date_from?: string | undefined;
        flat_number?: string | undefined;
        monthly_housing_expenses?:
            | {
                  amount?: number | undefined;
                  datetime?: string | undefined;
              }
            | undefined;
        property_value?:
            | {
                  amount?: number | undefined;
                  datetime?: string | undefined;
              }
            | undefined;
    }[];
};

export function getPeopleWithGuarantorStatus(people: LAPIPeople, uid: string) {
    const guarantors = people.filter(
        (person) =>
            person.roles &&
            person.roles.indexOf('guarantor') > -1 &&
            person.uid !== uid
    );

    return {
        email: _.flatten(guarantors.map((guarantor) => guarantor.emails)),
        phones: _.flatten(guarantors.map((guarantor) => guarantor.phones))
    };
}

export function getApplicantTitle(
    customer_state: GetStateByStateKeyResponse['data']
) {
    return _.get(getApplicant(customer_state), 'title');
}

export function getApplicantEmail(state: RootState) {
    const customer_state = _.get(state, 'api.customer_state.data');
    const primaryEmail = _.get(getApplicant(customer_state), 'emails', []).find(
        (email: { type: string }) => email.type === 'primary'
    );
    return _.get(primaryEmail, 'email');
}

export function getApplicantFirstName(
    customer_state: GetStateByStateKeyResponse['data']
) {
    return _.get(getApplicant(customer_state), 'first_name');
}

export function getApplicantLastName(
    customer_state: GetStateByStateKeyResponse['data']
) {
    return _.get(getApplicant(customer_state), 'last_name');
}

export function getApplicantFullName(
    customer_state: GetStateByStateKeyResponse['data']
) {
    const firstName = getApplicantFirstName(customer_state);
    const lastName = getApplicantLastName(customer_state);
    return `${firstName} ${lastName}`;
}

export function getApplicant(
    customer_state: GetStateByStateKeyResponse['data']
) {
    return (
        _.chain(customer_state)
            .get('application.people', [])
            // @ts-expect-error
            .find((person: LAPIPerson) => {
                const roles = _.get(person, 'roles', []);
                // @ts-expect-error
                return roles.indexOf('applicant') >= 0;
            })
            .value()
    );
}

export function getPersonFromUID(
    customer_state: GetStateByStateKeyResponse['data'],
    uid: string
) {
    return (
        _.chain(customer_state)
            .get('application.people', [])
            //@ts-expect-error
            .find((person: LAPIPerson) => person.uid === uid)
            .value()
    );
}

function isApplicant(person: LAPIPerson) {
    return _.some(person.roles, (role) => role === 'applicant');
}

function isGuarantor(person: LAPIPerson) {
    return person && _.some(person.roles, (role) => role === 'guarantor');
}

export function isShareholder(person: LAPIPerson) {
    return person && _.some(person.roles, (role) => role === 'shareholder');
}

export function isDirector(person: LAPIPerson) {
    return person && _.some(person.roles, (role) => role === 'director');
}

export function getPrimaryEmail(person: LAPIPerson) {
    return _.find(person.emails, (email) => email.type === 'primary');
}

export function getPrimaryEmailMarketing(person: LAPIPerson) {
    return _.chain(getPrimaryEmail(person))
        .get('marketing_opt_in.agreed')
        .value();
}

export function deserialisePeople(people: LAPIPeople) {
    return _.map(people, (person) => {
        return deserialisePerson(person);
    });
}

export function deserialisePerson(person: LAPIPerson): Person {
    return {
        uid: person.uid,
        title: _.get(person, 'title'),
        firstName: _.get(person, 'first_name'),
        lastName: _.get(person, 'last_name'),
        email: _.get(person, 'emails[0].email')!,
        emailUID: _.get(person, 'emails[0].uid')!,
        dateOfBirth: deserialisePersonBirthday(person),
        roles: _.get(person, 'roles', []),
        isApplicant: isApplicant(person),
        isGuarantor: isGuarantor(person),
        isShareholder: isShareholder(person),
        isDirector: isDirector(person),
        marketing: _.get(person, 'emails[0].marketing_opt_in.agreed')!,
        phones: _.get(person, 'phones'),
        addresses: _.get(person, 'residential_addresses')
    };
}

function deserialisePersonBirthday(person: LAPIPerson) {
    if (person.date_of_birth) {
        return {
            day: new Date(person.date_of_birth).toISOString().substring(8, 10),
            month: new Date(person.date_of_birth).toISOString().substring(5, 7),
            year: new Date(person.date_of_birth).toISOString().substring(0, 4)
        };
    }

    return {
        day: null,
        month: null,
        year: null
    };
}

export function serialisePerson(
    previousPersonState: LAPIPerson,
    person: Person
): LAPIPerson {
    const previousPhoneState = _.get(previousPersonState, 'phones', undefined);
    const previousEmailState = _.get(previousPersonState, 'emails', undefined);
    return {
        uid: person.uid || uuidv4(),
        title: _.lowerCase(person.title) as LAPIPerson['title'],
        first_name: person.firstName,
        last_name: person.lastName,
        date_of_birth: serialiseDateOfBirth(
            person.dateOfBirth as { year: string; month: string; day: string }
        ),
        roles: serialiseRoles(person),
        emails: serialiseEmail(previousEmailState, person),
        phones: serialisePhones(previousPhoneState, person.phones),
        residential_addresses: serialiseAddresses(person.addresses)
    };
}

export function serialiseRoles(person: Partial<Person>): LAPIPerson['roles'] {
    const roles = person.roles;
    const serialisedRoles = _.intersection(roles, ['applicant']);

    if (person.isGuarantor) {
        serialisedRoles.push('guarantor');
    }
    if (person.isDirector) {
        serialisedRoles.push('director');
    }
    if (person.isShareholder) {
        serialisedRoles.push('shareholder');
    }
    // @ts-expect-error Can probably fix this by just typing the serialisedRoles array and conditionally adding to it. Not tested though
    return serialisedRoles;
}

function serialiseAddresses(addresses: Person['addresses']) {
    return _.map(addresses, (address) => {
        return {
            uid: address.uid || uuidv4(),
            equifax_token: address.equifax_token,
            house_name: address.house_name,
            house_number: address.house_number,
            street_line_1: address.street_line_1,
            street_line_2: address.street_line_2,
            town: address.town,
            postcode: address.postcode,
            residential_status: address.residential_status
        };
    });
}

function serialiseDateOfBirth(dateOfBirth: {
    year: string;
    month: string;
    day: string;
}) {
    return `${dateOfBirth.year}-${dateOfBirth.month}-${dateOfBirth.day}`;
}

function serialiseEmail(
    previousEmailState: LAPIPerson['emails'],
    person: Person
): LAPIPerson['emails'] {
    const isEntrySame =
        !!_.find(previousEmailState, { email: person.email }) &&
        !!previousEmailState;

    return [
        {
            uid: getUUID(isEntrySame, person.emailUID),
            email: person.email,
            type: 'primary',
            marketing_opt_in: {
                agreed: !!person.marketing
            }
        }
    ];
}

function serialisePhones(
    previousPhoneState: LAPIPerson['phones'],
    phones: Person['phones']
): LAPIPerson['phones'] {
    // @ts-expect-error
    return _.map(phones, (phone) => {
        const isEntrySame =
            !!_.find(previousPhoneState, { number: phone.number }) &&
            !!previousPhoneState;

        return {
            number: phone.number,
            uid: getUUID(isEntrySame, phone.uid!),
            type: phone.type!
        };
    });
}

function getUUID(isEntrySame: boolean, existingUID: string) {
    if (isEntrySame) {
        return existingUID;
    }

    return uuidv4();
}

export function serialiseCompany(
    previousCompanyState: Company,
    newCompanyState: any
) {
    const serialisedNewCompanyState = _.cloneDeep(previousCompanyState!);

    const serialisedApiCompanyKeys = {
        tradingName: 'trading_name',
        companyName: 'registered_company_name',
        companyNumber: 'company_number',
        industry: 'industry'
    };

    Object.keys(newCompanyState).forEach((formFieldKey) => {
        // @ts-expect-error
        const serialisedKey = serialisedApiCompanyKeys[formFieldKey];
        if (serialisedKey) {
            // @ts-expect-error
            serialisedNewCompanyState[serialisedKey] =
                newCompanyState[formFieldKey];
        }
    });

    if (newCompanyState.tradingFrom) {
        serialisedNewCompanyState.trading_from_date = serialiseTradingFrom(
            newCompanyState.tradingFrom
        );
    }

    if (newCompanyState.annualTurnover) {
        serialisedNewCompanyState.last_12_months_turnover = {
            amount: parseInt(newCompanyState.annualTurnover, 10)
        };
    }

    if (newCompanyState.annualProfit) {
        serialisedNewCompanyState.last_12_months_profit = {
            amount: parseInt(newCompanyState.annualProfit, 10)
        };
    }

    if (newCompanyState.website) {
        serialisedNewCompanyState.website = serialiseWebsite(
            newCompanyState.website
        );
    } else if (hasCompanyValueBeenDeleted(newCompanyState.website)) {
        delete serialisedNewCompanyState.website;
    }

    return serialisedNewCompanyState;
}

function hasCompanyValueBeenDeleted(value: string) {
    if (value === '') {
        return true;
    }
}

export function serialiseWebsite(website: string) {
    if (hasWebsiteProtocol(website)) {
        return website;
    }

    return `https://${website}`;
}

function hasWebsiteProtocol(newWebsite: string) {
    return (
        newWebsite.indexOf('https://') !== -1 ||
        newWebsite.indexOf('http://') !== -1
    );
}

function serialiseTradingFrom(tradingFrom: string[]) {
    return _.values(tradingFrom).reverse().join('-');
}

export function deserialisePostcodeLookup(
    addresses: GetPostcodeLookupResponse['data']
) {
    return _.map(addresses, (address) => ({
        equifax_token: address.ptc_abs_code,
        ...address
    }));
}

export function deserialisePostcodeLookupResponse(
    response: GetPostcodeLookupResponse['data']
) {
    //TODO: this can be removed once we replaced all the references equifax_token to ptc_abs_code
    const responseAddresses = _.get(response, '[0].data', []);
    const deserialisedAddresses = deserialisePostcodeLookup(responseAddresses);
    const body = { data: [...deserialisedAddresses] };
    const meta = response[1];

    return [body, meta];
}

export function isTimeoutSessionResponse(action: {
    type: string;
    body: GetStateByStateKeyResponse['data'];
}) {
    return (
        _.chain(action)
            .get('response.data.errors')
            // @ts-expect-error lodash error
            .find(['code', 'ConflictError'])
            .value()
    );
}

export function validateCustomerState(
    state: GetStateByStateKeyResponse['data']
) {
    return !!(
        _.isObject(state.application) &&
        //@ts-expect-error UI object is not on LAPI type for state
        _.isObject(state.ui) &&
        _.isString(state.version)
    );
}

export function getPennyTransferDate(
    customer_state: GetStateByStateKeyResponse
) {
    return _.get(customer_state, 'ui.draw_down.penny_transfer_sent');
}

export function getCompanyType(customer_state: GetStateByStateKeyResponse) {
    return _.get(customer_state, 'application.company.type');
}

export function getCompanyTypeDefinition(
    customer_state: GetStateByStateKeyResponse
) {
    const companyTypes = {
        UK: companyTypesUK,
        DE: companyTypesDE
    }[getCountry()];
    const companyTypeObject = _.find(companyTypes, [
        'value',
        getCompanyType(customer_state)
    ]);
    return companyTypeObject;
}
